/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArticleInsightCitationDto,
  ArticleInsightOutlineDto,
  ArticleInsightQueryKbDto,
  ArticleInsightSectionDto,
  FactCheckErrorDto,
  FactCheckResultDto,
  GetDocumentTranslationResponseDto,
  GetGlossaryExtractionResponseDto,
  GetReferenceExtractionResponseDto,
  InferenceResponseDto,
  RuleCheckErrorDto,
  RuleCheckResultDto,
  SectionInsightDto,
  SseErrorDto,
  ThinkingContentDto,
  WorkflowChatMessageDto,
  WorkflowErrorDto,
} from '@ink-ai/insight-service-sdk';
import { RootState, getStore } from '../../reducers';
import { correction } from '../../reducers/correction';
import { getSocketConnection } from './socket';
import { useDispatch, useSelector } from 'react-redux';
import { webSocket } from '../../reducers/webSocket';
import { ChatStreamResponseDto, chat } from '../../reducers/chat';
import { compose } from '../../reducers/compose';
import { article } from '@ink-ai/portal/reducers/article';
import { inspect } from '@ink-ai/portal/reducers/inspect';
import { writingStyle } from '../../reducers/writing-style';
import { useStateAndRef } from '../hooks/useStateAndRef';
import { translation } from '../../reducers/translation';
import { app } from '@ink-ai/portal/reducers/app';
import { glossaryExtraction } from '@ink-ai/portal/reducers/glossary-extraction';
import { thinking } from '@ink-ai/portal/reducers/thinking';
import { workflow } from '@ink-ai/portal/reducers/workflow';

export const enum WsMessageType {
  INFERENCE_RESULT = 'inference-result',
  CHAT_RESULT = 'chat-result',
  TEXT_GENERATION_RESULT = 'text-generation-result',
  TEXT_ANALYSIS_RESULT = 'text-analysis-result',
  DOCUMENT_TRANSLATION_RESULT = 'document-translation-result',
  TEXT_GENERATION_INSIGHT_QUERY_KB = 'text-generation-insight_on_query_kb',
  TEXT_GENERATION_INSIGHT_CITATION = 'text-generation-insight_on_citation',
  TEXT_GENERATION_INSIGHT_OUTLINE = 'text-generation-insight_on_outline',
  TEXT_GENERATION_INSIGHT_SECTION = 'text-generation-insight_on_section',
  TEXT_GENERATION_INSIGHT = 'text-generation-insight',
  SECTION_GENERATION_ON_CITATION = 'section-generation_on_citation',
  SECTION_GENERATION_ON_CONTENT = 'section-generation_on_content',
  SECTION_GENERATION_ON_QUERY_KB = 'section-generation_on_query_kb',
  SECTION_GENERATION = 'section-generation',
  SECTION_GENERATION_ON_FULL_CONTENT = 'section-generation_on_full_content',
  SECTION_GENERATION_ON_ERROR = 'section-generation_on_error',
  RULE_CHECK_RESULT = 'rule-check-result',
  RULE_CHECK_ERROR = 'rule-check-error',
  FACT_CHECK_RESULT = 'fact-check-result',
  FACT_CHECK_ERROR = 'fact-check-error',
  GLOSSARY_EXTRACTION_RESULT = 'glossary-extraction-result',
  REFERENCE_EXTRACTION_RESULT = 'reference-extraction-result',
  THINKING_CONTENT = 'on_thinking',
  WORKFLOW_TEXT = 'chat-workflow-text',
  WORKFLOW_START = 'chat-workflow-start',
  WORKFLOW_FINISH = 'chat-workflow-finish',
  WORKFLOW_ERROR = 'chat-workflow-error',
  WORKFLOW_NODE_FINISH = 'chat-workflow-text-node-finished',
}

export type wsData<T extends WsMessageType, P> = {
  type: T;
  payload: P;
};

export type wsResponse =
  | wsData<WsMessageType.INFERENCE_RESULT, InferenceResponseDto>
  | wsData<WsMessageType.CHAT_RESULT, ChatStreamResponseDto>
  | wsData<WsMessageType.TEXT_GENERATION_RESULT, ChatStreamResponseDto>
  | wsData<WsMessageType.TEXT_ANALYSIS_RESULT, InferenceResponseDto>
  | wsData<
      WsMessageType.DOCUMENT_TRANSLATION_RESULT,
      GetDocumentTranslationResponseDto
    >
  | wsData<
      WsMessageType.TEXT_GENERATION_INSIGHT_QUERY_KB,
      ArticleInsightQueryKbDto
    >
  | wsData<
      WsMessageType.TEXT_GENERATION_INSIGHT_CITATION,
      ArticleInsightCitationDto
    >
  | wsData<
      WsMessageType.TEXT_GENERATION_INSIGHT_OUTLINE,
      ArticleInsightOutlineDto
    >
  | wsData<
      WsMessageType.TEXT_GENERATION_INSIGHT_SECTION,
      ArticleInsightSectionDto
    >
  | wsData<WsMessageType.TEXT_GENERATION_INSIGHT, ChatStreamResponseDto>
  | wsData<WsMessageType.SECTION_GENERATION, ChatStreamResponseDto>
  | wsData<
      WsMessageType.SECTION_GENERATION_ON_CITATION,
      ArticleInsightCitationDto
    >
  | wsData<WsMessageType.SECTION_GENERATION_ON_FULL_CONTENT, SectionInsightDto>
  | wsData<WsMessageType.SECTION_GENERATION_ON_CONTENT, SectionInsightDto>
  | wsData<WsMessageType.SECTION_GENERATION_ON_ERROR, SseErrorDto>
  | wsData<WsMessageType.RULE_CHECK_RESULT, RuleCheckResultDto>
  | wsData<WsMessageType.FACT_CHECK_RESULT, FactCheckResultDto>
  | wsData<WsMessageType.RULE_CHECK_ERROR, RuleCheckErrorDto>
  | wsData<WsMessageType.FACT_CHECK_ERROR, FactCheckErrorDto>
  | wsData<WsMessageType.GLOSSARY_EXTRACTION_RESULT,GetGlossaryExtractionResponseDto >
  | wsData<WsMessageType.REFERENCE_EXTRACTION_RESULT, GetReferenceExtractionResponseDto>
  | wsData<WsMessageType.THINKING_CONTENT, ThinkingContentDto>
  | wsData<WsMessageType.WORKFLOW_START, WorkflowChatMessageDto>
  | wsData<WsMessageType.WORKFLOW_FINISH, WorkflowChatMessageDto>
  | wsData<WsMessageType.WORKFLOW_ERROR, WorkflowErrorDto>
  | wsData<WsMessageType.WORKFLOW_NODE_FINISH, WorkflowChatMessageDto>
  | wsData<WsMessageType.WORKFLOW_TEXT, WorkflowChatMessageDto>;

const dispatchWsMessage = (message: wsResponse) => {
  const store = getStore();
  switch (message.type) {
    case WsMessageType.INFERENCE_RESULT:
      store.dispatch(correction.actions.updateGrammarList(message.payload));
      break;
    case WsMessageType.CHAT_RESULT:
      store.dispatch(chat.actions.updateChatResult(message.payload));
      break;
    case WsMessageType.TEXT_GENERATION_RESULT:
      store.dispatch(compose.actions.updateGeneratedText(message.payload));
      break;
    case WsMessageType.TEXT_ANALYSIS_RESULT:
      store.dispatch(
        writingStyle.actions.updateWritingStyleList(message.payload),
      );
      break;
    case WsMessageType.DOCUMENT_TRANSLATION_RESULT:
      store.dispatch(
        translation.actions.updateDocumentTranslationResult(message.payload),
      );
      if (message.payload.status === 'SUCCESS') {
        store.dispatch(
          app.actions.setGlobalMessage({
            message: `Document ${message.payload.name} translation successful`,
            status: 'success',
          }),
        );
      } else if (message.payload.status === 'ERROR') {
        app.actions.setGlobalMessage({
          message: `Document ${message.payload.name} translation failed: ${message.payload.errorMessage}`,
          status: 'error',
        });
      }
      break;

    case WsMessageType.GLOSSARY_EXTRACTION_RESULT:
      store.dispatch(
        glossaryExtraction.actions.updateGlossaryExtractionResult(message.payload),
      );
      if (message.payload.status === 'SUCCESS') {
        store.dispatch(
          app.actions.setGlobalMessage({
            message: `Document ${message.payload.name} glossary extraction successful`,
            status: 'success',
          }),
        );
      } else if (message.payload.status === 'ERROR') {
        app.actions.setGlobalMessage({
          message: `Document ${message.payload.name} glossary extraction  failed: ${message.payload.errorMessage}`,
          status: 'error',
        });
      }
      break;
    case WsMessageType.REFERENCE_EXTRACTION_RESULT:
      store.dispatch(
        glossaryExtraction.actions.updateGlossaryExtractionResult(message.payload as any),
      );
      if ((message.payload as any).status === 'SUCCESS') {
        store.dispatch(
          app.actions.setGlobalMessage({
            message: `Reference extraction succeeded`,
            status: 'success',
          }),
        );
      } else if ((message.payload as any).status === 'ERROR') {
        store.dispatch(
          app.actions.setGlobalMessage({
            message: `Reference extraction failed: ${(message.payload as any).errorMessage || ''}`,
            status: 'error',
          }),
        );
      }
      break;
    case WsMessageType.TEXT_GENERATION_INSIGHT_QUERY_KB:
      store.dispatch(article.actions.updateQueryKb(message.payload));
      break;
    case WsMessageType.TEXT_GENERATION_INSIGHT_CITATION:
      store.dispatch(article.actions.updateCitation(message.payload));
      break;
    case WsMessageType.TEXT_GENERATION_INSIGHT_OUTLINE:
      store.dispatch(article.actions.updateOutline(message.payload));
      break;
    case WsMessageType.TEXT_GENERATION_INSIGHT_SECTION:
      store.dispatch(article.actions.updateSection(message.payload));
      break;
    case WsMessageType.TEXT_GENERATION_INSIGHT:
      store.dispatch(article.actions.updateGeneratedText(message.payload));
      break;
    case WsMessageType.SECTION_GENERATION:
      store.dispatch(article.actions.updateSectionGeneration(message.payload));
      break;
    case WsMessageType.SECTION_GENERATION_ON_CONTENT:
      store.dispatch(article.actions.updatePreviewSectionText(message.payload));
      break;
    case WsMessageType.SECTION_GENERATION_ON_CITATION:
      store.dispatch(article.actions.setSectionCitations(message.payload));
      break;
    case WsMessageType.SECTION_GENERATION_ON_FULL_CONTENT:
      store.dispatch(article.actions.updateFullContentSection(message.payload));
      break;
    case WsMessageType.SECTION_GENERATION_ON_ERROR:
      store.dispatch(article.actions.sectionGenerationOnError());
      store.dispatch(
        app.actions.setGlobalMessage({
          message: `Error: ${message.payload.message}`,
          status: 'error',
        }),
      );
      break;
    case WsMessageType.RULE_CHECK_RESULT:
      store.dispatch(inspect.actions.updateRuleCheckResult(message.payload));
      break;
    case WsMessageType.FACT_CHECK_RESULT:
      store.dispatch(
        inspect.actions.updateFactCheckViolationResult(message.payload),
      );
      break;
    case WsMessageType.RULE_CHECK_ERROR:
      store.dispatch(inspect.actions.onRuleCheckTaskFail());
      console.error(message.payload.error.message);
      break;
    case WsMessageType.FACT_CHECK_ERROR:
      store.dispatch(inspect.actions.onFactCheckTaskFail());
      console.error(message.payload.error.message);
      break;
    case WsMessageType.THINKING_CONTENT:
      store.dispatch(thinking.actions.updateThinkingContent(message.payload));
      break;
    case WsMessageType.WORKFLOW_TEXT:
      store.dispatch(workflow.actions.onAiMessageUpdate(message.payload));
      break;
    case WsMessageType.WORKFLOW_START:
      store.dispatch(workflow.actions.onWorkflowStart(message.payload));
      break;
    case WsMessageType.WORKFLOW_FINISH:
      store.dispatch(workflow.actions.onWorkflowFinish(message.payload));
      break;
    case WsMessageType.WORKFLOW_ERROR:
      store.dispatch(workflow.actions.onWorkflowError(message.payload));
      break;
    case WsMessageType.WORKFLOW_NODE_FINISH:
      store.dispatch(workflow.actions.onNodeFinish(message.payload));
      break;
  }
};

const onMessage = (message: { data: string }) => {
  dispatchWsMessage(JSON.parse(message.data));
  getStore().dispatch(webSocket.actions.onMessage(JSON.parse(message.data)));
};

export const SocketHandler = () => {
  const [connection, setConnection, connectionRef] = useStateAndRef<
    WebSocket | undefined | null
  >();
  const dispatch = useDispatch<any>();
  const auth = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  // Timestamp of the first heartbeat check that saw the socket down. Reset to 0
  // whenever the socket is healthy. Used to bounce to /login if the connection
  // can't self-heal within a grace window (e.g. after the laptop wakes and the
  // WS keeps failing) — a fresh login reliably re-establishes everything.
  const downSinceRef = useRef<number>(0);
  // Only bounce to /login for a socket that WAS connected and then dropped
  // (e.g. after sleep). The very first connect can legitimately take a while
  // (cold start + token), so we never redirect while it has never connected —
  // that case is handled by getSocketConnection's own retry/reject.
  const everConnectedRef = useRef<boolean>(false);

  const setupConnection = useCallback(async () => {
    if (!auth.loggedIn) {
      return;
    }
    try {
      const wsConnection = await getSocketConnection();
      dispatch(webSocket.actions.onMessage());
      setConnection(wsConnection);
    } catch (error) {
      console.error(error);
      setConnection(null);
    }
  }, [setConnection, auth.loggedIn]);

  const onDisconnect = useCallback(() => {
    console.log('ws disconnected');
    if (connection?.readyState === WebSocket.OPEN) {
      connection.close();
    }
    setConnection(undefined);
  }, [setConnection, connection]);

  useEffect(() => {
    window.addEventListener('offline', onDisconnect);
    return () => window.removeEventListener('offline', onDisconnect);
  }, []);

  useEffect(() => {
    if (!connection) {
      // Retrigger setup when connection is missing. auth.loggedIn is a dep so
      // that a mount which happened while logged out (setupConnection returned
      // early, leaving connection === undefined) re-runs the moment the token
      // grant finishes and loggedIn flips true — otherwise, since this effect
      // only watched [connection] (which never changes from undefined), the
      // socket was never established on the initial post-redirect load and the
      // heartbeat logged "Waiting for initiation of ws" forever.
      setupConnection();
    } else {
      connection.addEventListener('close', onDisconnect);
      connection.addEventListener('message', onMessage);
    }

    return () => {
      connection?.removeEventListener('close', onDisconnect);
      connection?.removeEventListener('message', onMessage);
      connection?.close();
    };
  }, [connection, auth.loggedIn]);

  useEffect(() => {
    dispatch(
      webSocket.actions.changeConnected(
        connection?.readyState === WebSocket.OPEN,
      ),
    );
  }, [connection?.readyState]);

  // send and detect heartbeat
  useEffect(() => {
    const HEARTBEAT_INTERVAL = 15;   // 心跳间隔，控制多久没消息后发心跳
    const TIMEOUT_THRESHOLD = 20;    // 超时时间
    const CHECK_INTERVAL = 2000;    // 检查频率
    // If the socket can't self-heal within this window, bounce to /login — a
    // fresh login reliably rebuilds the session and the socket.
    const DOWN_TIMEOUT_MS = 10000;

    // Note the socket is down (record when it first went down). If it's been
    // down longer than DOWN_TIMEOUT_MS, give up self-healing and go to /login.
    const noteDownAndMaybeLogin = () => {
      // Never redirect if the socket has never connected yet — let
      // getSocketConnection's retry/reject drive the initial connect.
      if (!everConnectedRef.current) return false;
      const now = Date.now();
      if (downSinceRef.current === 0) downSinceRef.current = now;
      if (now - downSinceRef.current >= DOWN_TIMEOUT_MS) {
        console.log('WebSocket down > 10s, redirecting to /login');
        downSinceRef.current = 0;
        navigate('/login');
        return true;
      }
      return false;
    };

    const interval = setInterval(() => {
      const con = connectionRef.current;
      if (con === undefined) {
        console.log('Waiting for initiation of ws');
        noteDownAndMaybeLogin();
        return;
      }
      if (con === null) {
        console.log('No ws connection, resetting...');
        if (!noteDownAndMaybeLogin()) onDisconnect();
        return;
      }
      const { lastMessageAt } = getStore().getState().webSocket;
      const messageGap = (Date.now() - lastMessageAt) / 1000;
      if (con.readyState !== WebSocket.OPEN) {
        // socket exists but isn't open (connecting/closing/closed)
        if (!noteDownAndMaybeLogin()) onDisconnect();
      } else if (messageGap > TIMEOUT_THRESHOLD) {
        // healthy socket but silent too long — reconnect
        console.log(`WebSocket timeout: no message for ${messageGap}s, reconnecting...`);
        if (!noteDownAndMaybeLogin()) onDisconnect();
      } else {
        // socket is open and recently active — healthy, clear the down timer
        downSinceRef.current = 0;
        everConnectedRef.current = true;
        if (messageGap > HEARTBEAT_INTERVAL) {
          con.send(JSON.stringify({ action: 'heartbeat' }));
        }
      }
    }, CHECK_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [navigate]);

  return <></>;
};
